// The access token lives ONLY here — a module-level variable, never
// localStorage/sessionStorage/redux-persist. It's gone on hard refresh by
// design; AuthGate (see auth/AuthGate.tsx) re-derives it from the HttpOnly
// refresh cookie before the app renders anything protected.
let accessToken: string | null = null;

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  listeners.forEach((listener) => listener(token));
};

/** Notified on every login/refresh/logout — apiRequest.ts uses this to
 *  (re)schedule the proactive refresh timer without a circular import. */
export const subscribeAccessToken = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Refresh token persisted in localStorage so it survives page reloads.
// Required because cross-domain cookies are blocked by modern browsers.
const REFRESH_TOKEN_KEY = 'refreshToken';

export const getRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const setRefreshToken = (token: string | null): void => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

/** Reads the `exp` claim without verifying the signature — just for scheduling. */
export const decodeTokenExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
};
